import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Edit, Trash2, MapPin, Calendar, Clock, Tag, Award, Shirt, Package, Trophy, FileText, Settings } from 'lucide-react';
import { StatusBadge } from '@/components/shared';
import RaceTypesManager from '../RaceTypesManager';
import RegistrationsManager from '../RegistrationsManager';
import DiscountsManager from '../DiscountsManager';
import SponsorsManager from '../SponsorsManager';
import TshirtSizesManager from '../TshirtSizesManager';
import BibPickupManager from '../BibPickupManager';
import ResultsManager from '../ResultsManager';
import { EventDetailManagerViewProps } from './eventdetailmanager.props';

const EventDetailManagerView: React.FC<EventDetailManagerViewProps> = ({
  event,
  onBack,
  onEdit,
  onDelete,
  activeSubTab,
  setActiveSubTab,
}) => {
  return (
    <div className="space-y-6">
      {/* Back button */}
      <Button variant="ghost" onClick={onBack} className="gap-2">
        <ArrowLeft className="w-4 h-4" />
        Назад кон настани
      </Button>

      {/* Event header */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-start gap-6">
            {event.image && (
              <img
                src={event.image}
                alt={event.title}
                className="w-24 h-24 object-cover rounded-lg flex-shrink-0"
              />
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-2xl font-bold text-foreground">{event.title}</h2>
                  {event.subtitle && (
                    <p className="text-muted-foreground mt-1">{event.subtitle}</p>
                  )}
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <Button variant="outline" size="sm" onClick={() => onEdit(event)}>
                    <Edit className="w-4 h-4 mr-2" />
                    Измени
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onDelete(event.id)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Избриши
                  </Button>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-muted-foreground">
                {event.organizerName && (
                  <span className="flex items-center gap-1">
                    {event.organizerName}
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  {event.location}
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {event.date}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {event.time}
                </span>
              </div>
              <div className="flex items-center gap-2 mt-3">
                <StatusBadge
                  label={event.status === 'upcoming' ? 'Следен' : 'Поминат'}
                  variant={event.status === 'upcoming' ? 'success' : 'neutral'}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sub-tabs */}
      <Tabs value={activeSubTab} onValueChange={setActiveSubTab}>
        <div className="overflow-x-auto scrollbar-hide -mx-1 px-1">
          <TabsList className="bg-transparent border-b border-border rounded-none h-auto p-0 flex flex-nowrap gap-0 w-max min-w-full">
            <TabsTrigger value="race-types" className="flex items-center gap-2 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2.5 text-muted-foreground data-[state=active]:text-foreground transition-colors">
              <Settings className="w-4 h-4" />
              Типови на трки
            </TabsTrigger>
            <TabsTrigger value="registrations" className="flex items-center gap-2 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2.5 text-muted-foreground data-[state=active]:text-foreground transition-colors">
              <FileText className="w-4 h-4" />
              Регистрации
            </TabsTrigger>
            <TabsTrigger value="discounts" className="flex items-center gap-2 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2.5 text-muted-foreground data-[state=active]:text-foreground transition-colors">
              <Tag className="w-4 h-4" />
              Попусти
            </TabsTrigger>
            <TabsTrigger value="sponsors" className="flex items-center gap-2 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2.5 text-muted-foreground data-[state=active]:text-foreground transition-colors">
              <Award className="w-4 h-4" />
              Спонзори
            </TabsTrigger>
            <TabsTrigger value="tshirt-sizes" className="flex items-center gap-2 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2.5 text-muted-foreground data-[state=active]:text-foreground transition-colors">
              <Shirt className="w-4 h-4" />
              Маички
            </TabsTrigger>
            <TabsTrigger value="bib-pickup" className="flex items-center gap-2 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2.5 text-muted-foreground data-[state=active]:text-foreground transition-colors">
              <Package className="w-4 h-4" />
              Стартни броеви
            </TabsTrigger>
            <TabsTrigger value="results" className="flex items-center gap-2 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2.5 text-muted-foreground data-[state=active]:text-foreground transition-colors">
              <Trophy className="w-4 h-4" />
              Резултати
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="race-types" className="animate-in fade-in-0 duration-200">
          <RaceTypesManager raceId={event.id} raceName={event.title} embedded />
        </TabsContent>

        <TabsContent value="registrations" className="animate-in fade-in-0 duration-200">
          <RegistrationsManager raceId={event.id} />
        </TabsContent>

        <TabsContent value="discounts" className="animate-in fade-in-0 duration-200">
          <DiscountsManager raceId={event.id} />
        </TabsContent>

        <TabsContent value="sponsors" className="animate-in fade-in-0 duration-200">
          <SponsorsManager raceId={event.id} />
        </TabsContent>

        <TabsContent value="tshirt-sizes" className="animate-in fade-in-0 duration-200">
          <TshirtSizesManager raceId={event.id} />
        </TabsContent>

        <TabsContent value="bib-pickup" className="animate-in fade-in-0 duration-200">
          <BibPickupManager raceId={event.id} />
        </TabsContent>

        <TabsContent value="results" className="animate-in fade-in-0 duration-200">
          <ResultsManager raceId={event.id} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default EventDetailManagerView;
